/**
 * Policy editor.
 *
 * Read a policy, edit it into a NEW version, compare any two versions, and
 * record an approval decision.
 *
 * VERSIONS ARE IMMUTABLE
 * There is no "save over the current version" anywhere in this file, because
 * there is none on the server either. Saving always POSTs a new version; the
 * one you started from is untouched. That is what lets an auditor ask "what did
 * this policy say on 3 March?" and trust the answer. The UI says so, in the
 * editor, so nobody goes looking for an overwrite button or expects one.
 *
 * WHAT THE EDITOR CAN PRODUCE
 * The server sanitises every write with an allow-list (sanitizeContent in
 * api/src/modules/policies/service.ts) and silently discards anything else. A
 * silent discard looks exactly like data loss, so this file mirrors that list:
 * the toolbar only offers things on it, pasted content is filtered through the
 * same list before it lands, and on save the editor is rewritten to what will
 * really be stored — and the user is told if anything had to be removed.
 * The mirror is a usability aid. The SERVER remains the security boundary.
 *
 * NO EDITOR LIBRARY
 * A contentEditable region with document.execCommand. execCommand is formally
 * deprecated but is implemented identically by every shipping browser, has no
 * replacement API, and covers exactly the small command set needed here. A
 * full editor framework would add far more code than this whole page for
 * features the sanitiser would strip anyway.
 */

import {
  useCallback, useEffect, useMemo, useRef, useState,
  type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject,
} from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { can, LANGUAGES } from '@opencomply/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { cn } from '../lib/cn';
import { LanguageTag, useLanguageName } from '../components/LanguageTag';
import { LicenseAttribution, LICENSE_ATTRIBUTION_STRINGS } from '../components/LicenseAttribution';
import { Button, Card, CardHeader, ErrorNotice, Field, Modal, Pill, Skeleton, TONE_FOR_STATUS } from '../components/ui';

// ---------------------------------------------------------------------------
//  Strings (English defaults; keys are the i18n keys)
// ---------------------------------------------------------------------------

export const POLICY_EDITOR_STRINGS = {
  'policies.openEditor': 'Open',
  'policies.backToList': 'All policies',
  'policies.notFound': 'This policy could not be found.',
  'policies.loadFailed': 'Could not load this policy.',
  'policies.tabView': 'Read',
  'policies.tabEdit': 'Edit',
  'policies.tabCompare': 'Compare',
  'policies.tabReview': 'Review translations',
  // Linked languages
  'policies.languages': 'Languages',
  'policies.switchLanguage': 'Open the {{name}} version',
  'policies.thisLanguage': 'Showing the {{name}} version',
  'policies.addLanguage': 'Add a language',
  'policies.addLanguageTitle': 'Add a language',
  'policies.addLanguageBody': 'Create a linked copy of this policy in another language. It starts as its own draft, with its own approval, and is linked to this one so you can review them side by side. The text is pre-filled so you can translate in place.',
  'policies.addLanguageSelect': 'Language',
  'policies.addLanguageSubmit': 'Create linked draft',
  'policies.addLanguageNoneLeft': 'This policy already exists in every supported language.',
  'policies.addLanguageCreating': 'Creating…',
  'policies.addedNotice': 'Created a linked {{name}} draft. You are now editing it.',
  // Side-by-side review
  'policies.reviewIntro': 'Read two language versions at once and check they match. Each pane shows the latest version of that language.',
  'policies.reviewNeedTwo': 'This policy exists in only one language. Add a language to review translations side by side.',
  'policies.reviewLeft': 'Left',
  'policies.reviewRight': 'Right',
  'policies.reviewPaneFailed': 'Could not load this version.',
  'policies.currentVersion': 'Latest: {{version}}',
  'policies.latestBadge': 'Latest',
  'policies.cancel': 'Cancel',
  'policies.viewingVersion': 'Version',
  'policies.versionMeta': 'Created {{date}} by {{name}}',
  'policies.unknownAuthor': 'unknown',
  'policies.historyHint': 'Every version is kept exactly as it was saved.',
  'policies.viewThis': 'Read',
  'policies.startFromThis': 'Edit from this version',
  'policies.compareWithPrevious': 'Compare with previous',
  'policies.noApprover': 'No approver',
  // Immutability
  'policies.immutableTitle': 'Saving creates a new version',
  'policies.immutableBody': 'Published versions are never changed. When you save, your edits are stored as a new version and the one you started from stays exactly as it was. This is what makes the history trustworthy for an audit.',
  // Editor
  'policies.baseVersion': 'Start from version',
  'policies.editor': 'Policy text',
  'policies.editorPlaceholder': 'Write the policy here…',
  'policies.toolbar': 'Formatting',
  'policies.fmtBold': 'Bold',
  'policies.fmtItalic': 'Italic',
  'policies.fmtH2': 'Heading',
  'policies.fmtH3': 'Subheading',
  'policies.fmtBullets': 'Bulleted list',
  'policies.fmtNumbers': 'Numbered list',
  'policies.fmtLink': 'Link',
  'policies.fmtTable': 'Table',
  'policies.fmtUndo': 'Undo',
  'policies.fmtRedo': 'Redo',
  'policies.linkAddress': 'Link address',
  'policies.linkHint': 'https://, http:// or mailto: only. Other kinds of link are removed when saved.',
  'policies.linkApply': 'Apply',
  'policies.linkRemove': 'Remove link',
  'policies.linkInvalid': 'Enter an address starting with https://, http:// or mailto:.',
  'policies.tableColumns': 'Columns',
  'policies.tableRows': 'Rows (after header)',
  'policies.tableInsert': 'Insert table',
  'policies.supportedNote': 'Supported: bold, italic, headings, lists, tables and links. Anything else — colours, fonts, images — is not kept, so pasted text is cleaned to match.',
  'policies.removedNotice': '{{count}} unsupported items were removed so that what you see is exactly what is saved.',
  'policies.tooLong': 'The policy is too long to save ({{count}} of {{max}} characters).',
  // Save
  'policies.saveTitle': 'Save as a new version',
  'policies.versionLabel': 'New version number',
  'policies.versionHint': '3 to 20 characters, e.g. 1.1. Must not already exist.',
  'policies.versionTaken': 'Version {{version}} already exists. Choose another number.',
  'policies.versionLength': 'Use between 3 and 20 characters.',
  'policies.approverLabel': 'Approver',
  'policies.approverNone': 'No approval needed',
  'policies.approverHint': 'The approver is fixed when the version is created and cannot be reassigned afterwards. They are notified and are the only person who can decide.',
  'policies.approverUnavailable': 'You cannot list colleagues, so this version will be saved without an approver.',
  'policies.saveVersion': 'Save new version',
  'policies.nothingChanged': 'No changes yet. A version identical to the last one is not worth saving.',
  'policies.emptyContent': 'The policy is empty.',
  'policies.saved': 'Saved as version {{version}}. The previous version is unchanged.',
  'policies.unsavedChanges': 'You have unsaved changes.',
  'policies.discardTitle': 'Discard your edits?',
  'policies.discardBody': 'Loading a different version replaces the text you are editing. Your unsaved changes will be lost.',
  'policies.discardConfirm': 'Discard and continue',
  // Compare
  'policies.compareFrom': 'Before',
  'policies.compareTo': 'After',
  'policies.compareSwap': 'Swap',
  'policies.compareSame': 'Choose two different versions to compare.',
  'policies.compareNeedTwo': 'A policy needs at least two versions before they can be compared.',
  'policies.compareSummary': '{{changed}} changed, {{added}} added, {{removed}} removed, {{same}} unchanged',
  'policies.compareIdentical': 'These two versions have identical text.',
  'policies.compareShowAll': 'Show unchanged text',
  'policies.compareUnchangedRun': '{{count}} unchanged blocks',
  'policies.compareRemoved': 'Removed',
  'policies.compareAdded': 'Added',
  'policies.compareChanged': 'Changed',
  'policies.compareNote': 'Compares text and structure (headings, lists, table rows). A change that only affects formatting, such as making a word bold, is not highlighted.',
  'policies.compareLegend': 'Struck-through text was removed; underlined text was added.',
  // Approval
  'policies.approval': 'Approval',
  'policies.approvalNotRequired': 'No approval was requested for this version.',
  'policies.approvalPending': 'Waiting for {{name}} to decide.',
  'policies.approvalYourTurn': 'You are the assigned approver for this version.',
  'policies.approvalDecidedBy': '{{status}} by {{name}} on {{date}}',
  'policies.approvalComment': 'Comment',
  'policies.decisionFinal': 'A decision is final: it cannot be changed or repeated. If you reject, the author must save a new version.',
  'policies.approveTitle': 'Approve version {{version}}?',
  'policies.rejectTitle': 'Reject version {{version}}?',
  'policies.commentLabel': 'Comment',
  'policies.commentApproveHint': 'Optional.',
  'policies.commentRejectHint': 'Required: tell the author what needs to change.',
  'policies.commentRequired': 'Please explain the rejection.',
  'policies.decisionRecorded': 'Your decision has been recorded.',
  // Errors without a shared translation yet
  'policies.error.POLICY_NOT_FOUND': 'This policy could not be found.',
  'policies.error.POLICY_VERSION_EXISTS': 'That version number already exists.',
  'policies.error.POLICY_VERSION_TOO_SHORT': 'The version number is too short.',
  'policies.error.POLICY_APPROVAL_NOT_FOUND': 'There is no approval to decide on this version.',
  'policies.error.NOT_THE_ASSIGNED_APPROVER': 'Only the assigned approver can decide on this version.',
  'policies.error.APPROVAL_ALREADY_DECIDED': 'This version has already been decided.',
  'policies.error.POLICY_TRANSLATION_SAME_LANGUAGE': 'This policy is already in that language.',
  'policies.error.POLICY_TRANSLATION_LANGUAGE_EXISTS': 'A linked version already exists in that language.',
} as const;

export type PKey = keyof typeof POLICY_EDITOR_STRINGS;

// ---------------------------------------------------------------------------
//  Types
// ---------------------------------------------------------------------------

interface Approval {
  id: string;
  status: 'NOT_REQUIRED' | 'PENDING' | 'APPROVED' | 'REJECTED';
  approverId: string | null;
  comment: string | null;
  decidedAt: string | null;
  approver: { id: string; firstName: string; lastName: string } | null;
}
interface Version {
  id: string;
  version: string;
  contentHtml: string;
  createdAt: string;
  createdByName: string | null;
  approval: Approval | null;
}
/** One language version of this logical policy (a sibling in the translation group). */
interface TranslationSummary {
  id: string;
  language: string;
  name: string;
  version: string | null;
  approvalStatus: string | null;
  isActive: boolean;
  isCurrent: boolean;
}
interface PolicyDetail {
  id: string;
  name: string;
  description: string | null;
  documentCode: string | null;
  /** The language this policy is written in. */
  language: string;
  isActive: boolean;
  versions: Version[]; // newest first
  /**
   * The language versions in this policy's translation group, including this
   * one (flagged `isCurrent`). Empty/length-1 for a standalone policy.
   */
  translations: TranslationSummary[];
  /**
   * The template this policy was generated from, with its third-party source and
   * licence. Null for a blank policy or if the template has since been deleted
   * (the notice inside the document body is unaffected either way).
   */
  template: {
    id: string; name: string; license: string | null; sourceUrl: string | null;
    sourceAttribution: string | null; modifiedFromSource: boolean;
  } | null;
}
interface Member {
  id: string; firstName: string; lastName: string; role: string; isActive: boolean; email: string;
}

export const MAX_CONTENT_CHARS = 500_000; // the server's limit on contentHtml

// ---------------------------------------------------------------------------
//  Mirror of the server's sanitiser
//
//  Keep in step with sanitizeContent() in api/src/modules/policies/service.ts.
//  Built with DOMParser and createElement on an INERT document: nothing parsed
//  here can run script or fetch a resource, and only allow-listed elements and
//  attributes are ever copied across.
// ---------------------------------------------------------------------------

const ALLOWED_TAGS = new Set([
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'hr', 'div', 'span',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption',
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup',
  'blockquote', 'code', 'pre', 'a',
]);
const ALLOWED_ATTRIBUTES: Record<string, readonly string[]> = {
  a: ['href', 'title'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan', 'scope'],
};
const GLOBAL_ATTRIBUTES: readonly string[] = ['class'];
const ALLOWED_SCHEMES = ['http', 'https', 'mailto'];
/** Elements whose CONTENT is discarded too, not just the tag (as sanitize-html does). */
const DROP_WITH_CONTENT = new Set(['script', 'style', 'textarea', 'option', 'noscript', 'template']);

function isSafeHref(value: string): boolean {
  // Browsers ignore whitespace and control characters inside a scheme, so
  // "java\tscript:" still executes. Strip them before looking at the scheme.
  // eslint-disable-next-line no-control-regex
  const compact = value.replace(/[\u0000- \u007f-\u009f]/g, '');
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(compact);
  return !scheme || ALLOWED_SCHEMES.includes(scheme[1]!.toLowerCase());
}

interface CleanOptions {
  /** Add rel/target to links. For DISPLAY only; the server strips them on save. */
  forDisplay?: boolean;
  /** Drop class attributes (pasted Word/Docs markup is full of junk classes). */
  dropClass?: boolean;
}

function cleanInto(source: Node, target: Node, doc: Document, options: CleanOptions, stats: { removed: number }) {
  source.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(child.textContent ?? ''));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return; // comments etc.
    const el = child as Element;
    const tag = el.tagName.toLowerCase();

    if (DROP_WITH_CONTENT.has(tag)) { stats.removed += 1; return; }
    if (!ALLOWED_TAGS.has(tag)) {
      // Discard the tag, keep what was inside it — the same as the server.
      stats.removed += 1;
      cleanInto(el, target, doc, options, stats);
      return;
    }

    const out = doc.createElement(tag);
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const allowed = (ALLOWED_ATTRIBUTES[tag] ?? []).includes(name) || GLOBAL_ATTRIBUTES.includes(name);
      if (!allowed) {
        // `style` is not counted as content lost: nothing in this editor lets a
        // person set one, but Chrome adds inline styles of its own when it
        // merges pasted or edited text. Reporting those would cry wolf on
        // every save.
        if (name !== 'style') stats.removed += 1;
        continue;
      }
      if (name === 'class' && options.dropClass) continue;
      if (name === 'href' && !isSafeHref(attr.value)) { stats.removed += 1; continue; }
      out.setAttribute(name, attr.value);
    }
    if (tag === 'a' && options.forDisplay) {
      // The server's sanitiser intends to add these but its own attribute
      // filter then removes them, so stored links carry neither. Set here so a
      // policy link can never reach back through window.opener.
      out.setAttribute('rel', 'noopener noreferrer');
      out.setAttribute('target', '_blank');
    }
    cleanInto(el, out, doc, options, stats);
    target.appendChild(out);
  });
}

/** Parse HTML into a clean element tree built only from allowed nodes. */
export function sanitizeToElement(html: string, options: CleanOptions = {}): { root: HTMLElement; removed: number } {
  const parsed = new DOMParser().parseFromString(html, 'text/html'); // inert: no scripts run, nothing loads
  const doc = document.implementation.createHTMLDocument('');
  const root = doc.createElement('div');
  const stats = { removed: 0 };
  cleanInto(parsed.body, root, doc, options, stats);
  return { root, removed: stats.removed };
}

function normalizeHtml(html: string, options: CleanOptions = {}): { html: string; removed: number } {
  const { root, removed } = sanitizeToElement(html, options);
  return { html: root.innerHTML, removed };
}

export function isEffectivelyEmpty(html: string): boolean {
  const { root } = sanitizeToElement(html);
  return root.textContent!.trim() === '' && !root.querySelector('table,hr');
}

// ---------------------------------------------------------------------------
//  Diff
// ---------------------------------------------------------------------------

interface Block { kind: string; text: string; marker?: string }

const BLOCK_TAGS = new Set([
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li', 'blockquote', 'pre', 'caption', 'tr', 'div',
  'ul', 'ol', 'table', 'thead', 'tbody', 'hr',
]);

const collapse = (s: string) => s.replace(/\s+/g, ' ').trim();

/** Turn (already clean) policy HTML into an ordered list of text blocks. */
function extractBlocks(html: string): Block[] {
  const { root } = sanitizeToElement(html);
  const out: Block[] = [];

  const textOf = (el: Element, skipLists = false): string => {
    let s = '';
    el.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) s += n.textContent ?? '';
      else if (n.nodeType === Node.ELEMENT_NODE) {
        const c = n as Element;
        const tag = c.tagName.toLowerCase();
        if (skipLists && (tag === 'ul' || tag === 'ol')) return;
        s += tag === 'br' ? ' ' : ` ${textOf(c, skipLists)} `;
      }
    });
    return collapse(s);
  };
  const hasBlockChild = (el: Element) =>
    Array.from(el.children).some((c) => BLOCK_TAGS.has(c.tagName.toLowerCase()));

  const walk = (el: Element, ordered: boolean | null) => {
    let loose = '';
    const flush = () => {
      const text = collapse(loose);
      if (text) out.push({ kind: 'p', text });
      loose = '';
    };
    el.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) { loose += n.textContent ?? ''; return; }
      if (n.nodeType !== Node.ELEMENT_NODE) return;
      const c = n as Element;
      const tag = c.tagName.toLowerCase();
      if (!BLOCK_TAGS.has(tag)) { loose += tag === 'br' ? ' ' : ` ${textOf(c)} `; return; }
      flush();

      if (tag === 'hr') return;
      if (tag === 'ul' || tag === 'ol') { walk(c, tag === 'ol'); return; }
      if (tag === 'li') {
        const index = Array.from(el.children).indexOf(c) + 1;
        const text = textOf(c, true);
        if (text) out.push({ kind: 'li', text, marker: ordered ? `${index}.` : '•' });
        // Nested lists only. Recursing into the <li> itself would walk its own
        // text a second time and report every list item twice.
        Array.from(c.children)
          .filter((child) => ['ul', 'ol'].includes(child.tagName.toLowerCase()))
          .forEach((list) => walk(list, list.tagName.toLowerCase() === 'ol'));
        return;
      }
      if (tag === 'tr') {
        const cells = Array.from(c.children).map((cell) => textOf(cell));
        const text = cells.join(' | ');
        if (cells.some(Boolean)) out.push({ kind: 'row', text });
        return;
      }
      if (['table', 'thead', 'tbody', 'div'].includes(tag) || (['blockquote'].includes(tag) && hasBlockChild(c))) {
        walk(c, null);
        return;
      }
      const text = textOf(c);
      if (text) out.push({ kind: tag, text });
    });
    flush();
  };

  walk(root, null);
  return out;
}

type Op = { op: 'eq' | 'del' | 'ins'; i: number; j: number };

/** Minimal edit script between two sequences (LCS), with prefix/suffix trimming. */
function editScript(a: string[], b: string[]): Op[] {
  const ops: Op[] = [];
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) { endA -= 1; endB -= 1; }

  for (let k = 0; k < start; k += 1) ops.push({ op: 'eq', i: k, j: k });

  const n = endA - start;
  const m = endB - start;
  // The DP table is n*m cells. Past a few million (a very long policy that was
  // wholly rewritten) it is not worth the memory: report the middle as replaced.
  if (n * m > 4_000_000) {
    for (let i = 0; i < n; i += 1) ops.push({ op: 'del', i: start + i, j: start });
    for (let j = 0; j < m; j += 1) ops.push({ op: 'ins', i: start + n, j: start + j });
  } else {
    const w = m + 1;
    const dp = new Uint32Array((n + 1) * w);
    for (let i = n - 1; i >= 0; i -= 1) {
      for (let j = m - 1; j >= 0; j -= 1) {
        dp[i * w + j] = a[start + i] === b[start + j]
          ? dp[(i + 1) * w + j + 1]! + 1
          : Math.max(dp[(i + 1) * w + j]!, dp[i * w + j + 1]!);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (a[start + i] === b[start + j]) { ops.push({ op: 'eq', i: start + i, j: start + j }); i += 1; j += 1; }
      else if (dp[(i + 1) * w + j]! >= dp[i * w + j + 1]!) { ops.push({ op: 'del', i: start + i, j: start + j }); i += 1; }
      else { ops.push({ op: 'ins', i: start + i, j: start + j }); j += 1; }
    }
    while (i < n) { ops.push({ op: 'del', i: start + i, j: start + j }); i += 1; }
    while (j < m) { ops.push({ op: 'ins', i: start + i, j: start + j }); j += 1; }
  }

  for (let k = 0; k < a.length - endA; k += 1) ops.push({ op: 'eq', i: endA + k, j: endB + k });
  return ops;
}

interface Token { text: string; changed: boolean }

/** Word-level diff of two texts: which words are unique to each side. */
function wordDiff(left: string, right: string): { left: Token[]; right: Token[]; similarity: number } {
  const a = left.split(' ').filter(Boolean);
  const b = right.split(' ').filter(Boolean);
  if (a.length > 600 || b.length > 600) {
    // Too big to diff word-by-word cheaply; show the whole block as changed.
    return { left: a.map((text) => ({ text, changed: true })), right: b.map((text) => ({ text, changed: true })), similarity: 0.5 };
  }
  const ops = editScript(a, b);
  const l: Token[] = a.map((text) => ({ text, changed: true }));
  const r: Token[] = b.map((text) => ({ text, changed: true }));
  let common = 0;
  for (const o of ops) {
    if (o.op === 'eq') { l[o.i]!.changed = false; r[o.j]!.changed = false; common += 1; }
  }
  const total = a.length + b.length;
  return { left: l, right: r, similarity: total === 0 ? 1 : (2 * common) / total };
}

/** Every word of a block flagged as changed: used for wholly removed/added blocks. */
const wholeBlock = (b: Block): Token[] => b.text.split(' ').filter(Boolean).map((text) => ({ text, changed: true }));

interface DiffRow {
  status: 'same' | 'changed' | 'removed' | 'added';
  left?: Block;
  right?: Block;
  leftTokens?: Token[];
  rightTokens?: Token[];
}

/** Two blocks count as "the same block, edited" only if they mostly agree. */
const PAIR_SIMILARITY = 0.35;

function diffBlocks(before: Block[], after: Block[]): DiffRow[] {
  const key = (b: Block) => `${b.kind}\u0000${b.text}`;
  const ops = editScript(before.map(key), after.map(key));
  const rows: DiffRow[] = [];

  let dels: number[] = [];
  let inss: number[] = [];
  const flushHunk = () => {
    // Pair each addition with the nearest later-or-equal deletion that is
    // similar enough, keeping order. What cannot be paired is a pure add/remove.
    let p = 0;
    for (const j of inss) {
      let matched = -1;
      let best: ReturnType<typeof wordDiff> | null = null;
      for (let d = p; d < dels.length; d += 1) {
        const l = before[dels[d]!]!;
        const r = after[j]!;
        const wd = wordDiff(l.text, r.text);
        if (wd.similarity >= PAIR_SIMILARITY) { matched = d; best = wd; break; }
      }
      if (matched >= 0 && best) {
        for (let d = p; d < matched; d += 1) rows.push({ status: 'removed', left: before[dels[d]!]!, leftTokens: wholeBlock(before[dels[d]!]!) });
        rows.push({
          status: 'changed', left: before[dels[matched]!]!, right: after[j]!,
          leftTokens: best.left, rightTokens: best.right,
        });
        p = matched + 1;
      } else {
        rows.push({ status: 'added', right: after[j]!, rightTokens: wholeBlock(after[j]!) });
      }
    }
    for (let d = p; d < dels.length; d += 1) rows.push({ status: 'removed', left: before[dels[d]!]!, leftTokens: wholeBlock(before[dels[d]!]!) });
    dels = [];
    inss = [];
  };

  for (const o of ops) {
    if (o.op === 'del') dels.push(o.i);
    else if (o.op === 'ins') inss.push(o.j);
    else {
      flushHunk();
      rows.push({ status: 'same', left: before[o.i]!, right: after[o.j]! });
    }
  }
  flushHunk();
  return rows;
}

// ---------------------------------------------------------------------------
//  Shared presentation of policy HTML
// ---------------------------------------------------------------------------

/**
 * Tailwind's preflight strips list bullets, heading sizes and table borders, so
 * policy content needs them put back. One string, used by the reader and the
 * editor, so the two look the same — what you type is what will be read.
 */
const PROSE = cn(
  'text-sm sm:text-[15px] leading-relaxed text-ink break-words',
  '[&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mt-5 [&_h1]:mb-2',
  '[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-5 [&_h2]:mb-2',
  '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5',
  '[&_h4]:font-semibold [&_h4]:mt-3 [&_h5]:font-semibold [&_h6]:font-semibold',
  '[&_p]:my-2 [&_div]:my-1',
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_li]:my-0.5',
  '[&_a]:text-brand [&_a]:underline [&_a]:underline-offset-2',
  '[&_table]:w-full [&_table]:border-collapse [&_table]:my-3 [&_table]:text-sm',
  '[&_td]:border [&_td]:border-line [&_td]:px-2 [&_td]:py-1.5 [&_td]:align-top [&_td]:min-w-[5rem]',
  '[&_th]:border [&_th]:border-line [&_th]:px-2 [&_th]:py-1.5 [&_th]:bg-surface-sunken [&_th]:text-left [&_th]:font-semibold [&_th]:min-w-[5rem]',
  '[&_caption]:text-xs [&_caption]:text-ink-muted [&_caption]:mb-1',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-line [&_blockquote]:pl-3 [&_blockquote]:text-ink-secondary',
  '[&_pre]:bg-surface-sunken [&_pre]:rounded-lg [&_pre]:p-3 [&_pre]:overflow-x-auto [&_pre]:text-xs [&_pre]:font-mono',
  '[&_code]:font-mono [&_code]:text-[0.85em] [&_code]:bg-surface-sunken [&_code]:px-1 [&_code]:rounded',
  '[&_hr]:my-4 [&_hr]:border-line',
);

/**
 * Read-only rendering of one stored version.
 *
 * dangerouslySetInnerHTML is used here, and is safe here, for two independent
 * reasons: (1) contentHtml was passed through the server's allow-list sanitiser
 * when it was WRITTEN, so what is stored has no scripts, event handlers, styles
 * or javascript:/data: URLs; and (2) it is re-run through this file's mirror of
 * that allow-list, which rebuilds it from allow-listed elements only, before it
 * reaches the DOM. A regression in one layer does not expose users. Nothing
 * that a person is currently typing ever passes through here.
 */
function PolicyBody({ html }: { html: string }) {
  const safe = useMemo(() => normalizeHtml(html, { forDisplay: true }).html, [html]);
  return (
    <div className="overflow-x-auto">
      <div className={PROSE} dangerouslySetInnerHTML={{ __html: safe }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Rich-text editor
// ---------------------------------------------------------------------------

export interface EditorStatus { changed: boolean; empty: boolean; length: number }

interface ActiveState { bold: boolean; italic: boolean; h2: boolean; h3: boolean; ul: boolean; ol: boolean; link: boolean }
const NO_ACTIVE: ActiveState = { bold: false, italic: false, h2: false, h3: false, ul: false, ol: false, link: false };

function escapeHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function RichTextEditor({ initialHtml, editorRef, onStatus, tp, disabled }: {
  /** Read once, on mount. Remount (change `key`) to load different content. */
  initialHtml: string;
  editorRef: RefObject<HTMLDivElement | null>;
  onStatus: (status: EditorStatus) => void;
  tp: (key: PKey, options?: Record<string, unknown>) => string;
  disabled?: boolean;
}) {
  const baseline = useRef('');
  const [isEmpty, setIsEmpty] = useState(true);
  const [active, setActive] = useState<ActiveState>(NO_ACTIVE);
  const [panel, setPanel] = useState<'link' | 'table' | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState(false);
  const [cols, setCols] = useState(3);
  const [rowCount, setRowCount] = useState(2);
  // The selection is lost when focus moves into the link/table inputs, so it is
  // remembered here and restored before the command runs.
  const savedRange = useRef<Range | null>(null);
  const timer = useRef<number | undefined>(undefined);

  // Load the initial content once. It is built from allow-listed nodes only.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const { root } = sanitizeToElement(initialHtml);
    baseline.current = root.innerHTML;
    el.replaceChildren(...Array.from(root.childNodes).map((n) => document.importNode(n, true)));
    setIsEmpty(isEffectivelyEmpty(root.innerHTML));
    onStatus({ changed: false, empty: isEffectivelyEmpty(root.innerHTML), length: root.innerHTML.length });
    // Initial content is intentionally read once; the parent remounts to change it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  /** Recompute changed/empty/length shortly after typing stops, not per keystroke. */
  const scheduleStatus = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const el = editorRef.current;
      if (!el) return;
      const { html } = normalizeHtml(el.innerHTML);
      const empty = isEffectivelyEmpty(html);
      setIsEmpty(empty);
      onStatus({ changed: html !== baseline.current, empty, length: html.length });
    }, 250);
  }, [editorRef, onStatus]);

  const inEditor = (node: Node | null) => !!node && !!editorRef.current?.contains(node);

  /** Which formats apply at the caret, so toolbar buttons can show pressed state. */
  const refreshActive = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !inEditor(sel.anchorNode)) return;
    let node: Node | null = sel.anchorNode;
    const seen = { h2: false, h3: false, ul: false, ol: false, link: false };
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = (node as Element).tagName.toLowerCase();
        if (tag === 'h2') seen.h2 = true;
        if (tag === 'h3') seen.h3 = true;
        if (tag === 'ul') seen.ul = true;
        if (tag === 'ol') seen.ol = true;
        if (tag === 'a') seen.link = true;
      }
      node = node.parentNode;
    }
    let bold = false;
    let italic = false;
    try {
      bold = document.queryCommandState('bold');
      italic = document.queryCommandState('italic');
    } catch { /* unsupported in this browser: leave unpressed */ }
    setActive({ bold, italic, ...seen });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshActive);
    return () => document.removeEventListener('selectionchange', refreshActive);
  }, [refreshActive]);

  /** Put the caret in the editor if it is not already there. */
  const ensureFocus = () => {
    const el = editorRef.current;
    if (!el) return false;
    const sel = window.getSelection();
    if (!sel) return false;
    if (sel.rangeCount === 0 || !inEditor(sel.anchorNode)) {
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    return true;
  };

  const exec = (command: string, value?: string) => {
    if (!ensureFocus()) return;
    document.execCommand(command, false, value);
    scheduleStatus();
    refreshActive();
  };

  const toggleBlock = (tag: 'h2' | 'h3', on: boolean) => exec('formatBlock', on ? '<p>' : `<${tag}>`);

  const rememberSelection = () => {
    const sel = window.getSelection();
    savedRange.current = sel && sel.rangeCount > 0 && inEditor(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
  };
  const restoreSelection = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    } else {
      ensureFocus();
    }
  };

  const openLink = () => {
    rememberSelection();
    const anchor = savedRange.current?.startContainer;
    const existing = anchor
      ? (anchor.nodeType === Node.ELEMENT_NODE ? (anchor as Element) : anchor.parentElement)?.closest('a')
      : null;
    setLinkUrl(existing?.getAttribute('href') ?? '');
    setLinkError(false);
    setPanel(panel === 'link' ? null : 'link');
  };

  const applyLink = () => {
    let url = linkUrl.trim();
    // "example.com" is what people type; make it a real address.
    if (url && !/^[a-z][a-z0-9+.-]*:/i.test(url)) url = `https://${url}`;
    if (!/^(https?:\/\/|mailto:)/i.test(url) || !isSafeHref(url)) { setLinkError(true); return; }
    restoreSelection();
    const sel = window.getSelection();
    if (sel && sel.isCollapsed) {
      // Nothing selected: insert the address itself as the link text.
      document.execCommand('insertHTML', false, `<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`);
    } else {
      document.execCommand('createLink', false, url);
    }
    setPanel(null);
    scheduleStatus();
    refreshActive();
  };

  const removeLink = () => {
    restoreSelection();
    document.execCommand('unlink');
    setPanel(null);
    scheduleStatus();
    refreshActive();
  };

  const insertTable = () => {
    restoreSelection();
    const c = Math.min(8, Math.max(1, cols));
    const r = Math.min(20, Math.max(1, rowCount));
    const head = Array.from({ length: c }, () => '<th scope="col"><br></th>').join('');
    const cells = Array.from({ length: c }, () => '<td><br></td>').join('');
    const body = Array.from({ length: r }, () => `<tr>${cells}</tr>`).join('');
    // The trailing paragraph gives the caret somewhere to go below the table.
    document.execCommand('insertHTML', false, `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table><p><br></p>`);
    setPanel(null);
    scheduleStatus();
  };

  /**
   * Pasted content is rebuilt from allow-listed nodes before it is inserted.
   * Without this, text pasted from Word or a web page arrives full of styles
   * and classes that look fine on screen and are then silently dropped on save.
   */
  const onPaste = (e: React.ClipboardEvent) => {
    const html = e.clipboardData.getData('text/html');
    if (!html) return; // plain text: the browser's default is right
    e.preventDefault();
    const { html: cleaned } = normalizeHtml(html, { dropClass: true });
    document.execCommand('insertHTML', false, cleaned);
    scheduleStatus();
  };

  const onFocus = () => {
    try {
      // <p> on Enter, not <div>: <p> is what the policy templates use.
      document.execCommand('defaultParagraphSeparator', false, 'p');
      // Bold/italic as <b>/<i> tags (allowed) rather than style spans (stripped).
      document.execCommand('styleWithCSS', false, 'false');
      document.execCommand('enableObjectResizing', false, 'false');
      document.execCommand('enableInlineTableEditing', false, 'false');
    } catch { /* Not every browser knows every command; none is essential. */ }
  };

  // Buttons must not take focus, or the editor blurs and the selection they are
  // about to act on is lost.
  const keepSelection = (e: ReactMouseEvent) => e.preventDefault();

  return (
    // On a phone the editor bleeds to the edge of its card (-mx-4 undoes the
    // card's padding): 30px of writing width and toolbar width matter more
    // than a rounded border when the whole screen is 375px.
    <div className="rounded-xl border border-line bg-surface-raised max-sm:-mx-4 max-sm:rounded-none max-sm:border-x-0">
      {/*
        MOBILE TOOLBAR
        A phone gives us ~340px and a keyboard that covers half the screen, so:
          * one horizontally scrolling row (never wrapping into a tall block
            that eats the editing area);
          * sticky to the TOP, not the bottom: the bottom is where the
            keyboard and the app's tab bar are;
          * 44px touch targets, grouped by purpose, most-used first, so the
            things that fall off the right edge are the rarely-used ones
            (undo/redo, which the keyboard also offers).
        It is a labelled toolbar so a screen reader announces it as one.
      */}
      <div
        role="toolbar"
        aria-label={tp('policies.toolbar')}
        className="sticky top-14 lg:top-0 z-10 flex items-center gap-0 sm:gap-0.5 overflow-x-auto
                   px-1 sm:px-1.5 py-1 border-b border-line bg-surface-raised rounded-t-xl max-sm:rounded-none"
        style={{ scrollbarWidth: 'none' }}
      >
        <ToolButton label={tp('policies.fmtBold')} pressed={active.bold} disabled={disabled}
          onMouseDown={keepSelection} onClick={() => exec('bold')}>
          <span className="font-bold">B</span>
        </ToolButton>
        <ToolButton label={tp('policies.fmtItalic')} pressed={active.italic} disabled={disabled}
          onMouseDown={keepSelection} onClick={() => exec('italic')}>
          <span className="italic font-serif">I</span>
        </ToolButton>
        <Divider />
        <ToolButton label={tp('policies.fmtH2')} pressed={active.h2} disabled={disabled}
          onMouseDown={keepSelection} onClick={() => toggleBlock('h2', active.h2)}>
          <span className="text-xs font-semibold">H2</span>
        </ToolButton>
        <ToolButton label={tp('policies.fmtH3')} pressed={active.h3} disabled={disabled}
          onMouseDown={keepSelection} onClick={() => toggleBlock('h3', active.h3)}>
          <span className="text-xs font-semibold">H3</span>
        </ToolButton>
        <Divider />
        <ToolButton label={tp('policies.fmtBullets')} pressed={active.ul} disabled={disabled}
          onMouseDown={keepSelection} onClick={() => exec('insertUnorderedList')}>
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
            <path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r="1" fill="currentColor" /><circle cx="4.5" cy="12" r="1" fill="currentColor" /><circle cx="4.5" cy="18" r="1" fill="currentColor" />
          </svg>
        </ToolButton>
        <ToolButton label={tp('policies.fmtNumbers')} pressed={active.ol} disabled={disabled}
          onMouseDown={keepSelection} onClick={() => exec('insertOrderedList')}>
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
            <path d="M10 6h10M10 12h10M10 18h10" /><path d="M4 5l1.5-1v4M4 13c0-1 2.5-1 2.5 0S4 15 4 16h2.5M4 18.5h2.5l-1 1.2a1 1 0 1 1-1 1" strokeWidth="1.25" />
          </svg>
        </ToolButton>
        <Divider />
        <ToolButton label={tp('policies.fmtLink')} pressed={active.link || panel === 'link'} disabled={disabled}
          onMouseDown={keepSelection} onClick={openLink}>
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
          </svg>
        </ToolButton>
        <ToolButton label={tp('policies.fmtTable')} pressed={panel === 'table'} disabled={disabled}
          onMouseDown={keepSelection} onClick={() => { rememberSelection(); setPanel(panel === 'table' ? null : 'table'); }}>
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M3 15h18M9 4v16M15 4v16" />
          </svg>
        </ToolButton>
        <Divider />
        <ToolButton label={tp('policies.fmtUndo')} disabled={disabled}
          onMouseDown={keepSelection} onClick={() => exec('undo')}>
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M9 14 4 9l5-5" /><path d="M4 9h10a6 6 0 0 1 0 12h-3" />
          </svg>
        </ToolButton>
        <ToolButton label={tp('policies.fmtRedo')} disabled={disabled}
          onMouseDown={keepSelection} onClick={() => exec('redo')}>
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="m15 14 5-5-5-5" /><path d="M20 9H10a6 6 0 0 0 0 12h3" />
          </svg>
        </ToolButton>
      </div>

      {panel === 'link' && (
        <form
          className="p-3 border-b border-line bg-surface-sunken space-y-2"
          onSubmit={(e) => { e.preventDefault(); applyLink(); }}
        >
          <label className="oc-label" htmlFor="pe-link">{tp('policies.linkAddress')}</label>
          <input
            id="pe-link" className="oc-input" type="text" inputMode="url" autoComplete="off"
            autoFocus placeholder="https://" value={linkUrl}
            onChange={(e) => { setLinkUrl(e.target.value); setLinkError(false); }}
          />
          {linkError
            ? <p role="alert" className="text-xs text-critical">{tp('policies.linkInvalid')}</p>
            : <p className="text-xs text-ink-muted">{tp('policies.linkHint')}</p>}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" size="sm">{tp('policies.linkApply')}</Button>
            {active.link && <Button size="sm" onClick={removeLink}>{tp('policies.linkRemove')}</Button>}
            <Button size="sm" variant="ghost" onClick={() => setPanel(null)}>{tp('policies.cancel')}</Button>
          </div>
        </form>
      )}

      {panel === 'table' && (
        <form
          className="p-3 border-b border-line bg-surface-sunken"
          onSubmit={(e) => { e.preventDefault(); insertTable(); }}
        >
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <div>
              <label className="oc-label" htmlFor="pe-cols">{tp('policies.tableColumns')}</label>
              <input id="pe-cols" className="oc-input" type="number" inputMode="numeric" min={1} max={8}
                value={cols} onChange={(e) => setCols(Number(e.target.value))} />
            </div>
            <div>
              <label className="oc-label" htmlFor="pe-rows">{tp('policies.tableRows')}</label>
              <input id="pe-rows" className="oc-input" type="number" inputMode="numeric" min={1} max={20}
                value={rowCount} onChange={(e) => setRowCount(Number(e.target.value))} />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button type="submit" variant="primary" size="sm">{tp('policies.tableInsert')}</Button>
            <Button size="sm" variant="ghost" onClick={() => setPanel(null)}>{tp('policies.cancel')}</Button>
          </div>
        </form>
      )}

      <div className="relative">
        {isEmpty && (
          <p aria-hidden className="pointer-events-none absolute left-3 top-3 text-sm text-ink-muted">
            {tp('policies.editorPlaceholder')}
          </p>
        )}
        <div className="overflow-x-auto">
          <div
            ref={editorRef}
            role="textbox"
            aria-multiline="true"
            aria-label={tp('policies.editor')}
            contentEditable={!disabled}
            suppressContentEditableWarning
            spellCheck
            onInput={scheduleStatus}
            onPaste={onPaste}
            onFocus={onFocus}
            // 16px on phones: below that iOS Safari zooms the page on focus.
            // No inner scroll on a phone: a scrolling box inside a scrolling page traps
            // the thumb. The page scrolls and the toolbar stays stuck to the top.
            className={cn(PROSE, 'min-h-[16rem] sm:max-h-[70dvh] sm:overflow-y-auto px-3 py-3 outline-none text-base sm:text-[15px]')}
          />
        </div>
      </div>
    </div>
  );
}

function Divider() {
  return <span aria-hidden className="hidden sm:block w-px h-6 bg-line mx-1 shrink-0" />;
}

function ToolButton({ label, pressed, disabled, onClick, onMouseDown, children }: {
  label: string;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  onMouseDown: (e: ReactMouseEvent) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      // aria-pressed only where the control is a toggle.
      aria-pressed={pressed === undefined ? undefined : pressed}
      disabled={disabled}
      onMouseDown={onMouseDown}
      onClick={onClick}
      className={cn(
        'shrink-0 min-w-[2.5rem] sm:min-w-[2.75rem] min-h-[2.75rem] rounded-lg grid place-items-center text-sm transition-colors',
        pressed ? 'bg-brand/15 text-brand' : 'text-ink-secondary hover:bg-surface-sunken hover:text-ink',
        'disabled:opacity-40',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
//  Version comparison
// ---------------------------------------------------------------------------

function BlockView({ block, tokens, side }: { block: Block; tokens?: Token[]; side: 'left' | 'right' }) {
  const words = tokens ?? block.text.split(' ').map((text) => ({ text, changed: false }));
  const body = (
    <>
      {words.map((w, i) => (
        <span
          key={i}
          className={cn(
            w.changed && side === 'left' && 'bg-critical/20 line-through decoration-critical',
            w.changed && side === 'right' && 'bg-good/25 underline decoration-good underline-offset-2',
          )}
        >
          {w.text}{' '}
        </span>
      ))}
    </>
  );
  switch (block.kind) {
    case 'h1': return <p className="text-lg font-semibold">{body}</p>;
    case 'h2': return <p className="text-base font-semibold">{body}</p>;
    case 'h3': case 'h4': case 'h5': case 'h6': return <p className="text-sm font-semibold">{body}</p>;
    case 'li': return <p className="pl-1"><span className="text-ink-muted mr-1.5">{block.marker}</span>{body}</p>;
    case 'row': return <p className="font-mono text-xs">{body}</p>;
    case 'blockquote': return <p className="border-l-4 border-line pl-2 text-ink-secondary">{body}</p>;
    case 'pre': return <p className="font-mono text-xs">{body}</p>;
    default: return <p>{body}</p>;
  }
}

function CompareView({ versions, tp }: {
  versions: Version[];
  tp: (key: PKey, options?: Record<string, unknown>) => string;
}) {
  // Versions arrive newest-first; default to "the one before latest -> latest".
  const [fromId, setFromId] = useState(versions[1]?.id ?? '');
  const [toId, setToId] = useState(versions[0]?.id ?? '');
  const [showAll, setShowAll] = useState(false);

  const from = versions.find((v) => v.id === fromId);
  const to = versions.find((v) => v.id === toId);

  const rows = useMemo(
    () => (from && to && from.id !== to.id ? diffBlocks(extractBlocks(from.contentHtml), extractBlocks(to.contentHtml)) : []),
    [from, to],
  );
  const counts = useMemo(() => {
    const c = { changed: 0, added: 0, removed: 0, same: 0 };
    for (const r of rows) c[r.status] += 1;
    return c;
  }, [rows]);

  if (versions.length < 2) {
    return <p className="text-sm text-ink-secondary">{tp('policies.compareNeedTwo')}</p>;
  }

  // Collapse long runs of unchanged text into one line, keeping one block of
  // context either side, so a 40-page policy with 3 edits shows the 3 edits.
  const display: Array<{ row: DiffRow; index: number } | { collapsed: number; index: number }> = [];
  if (showAll) {
    rows.forEach((row, index) => display.push({ row, index }));
  } else {
    let i = 0;
    while (i < rows.length) {
      if (rows[i]!.status !== 'same') { display.push({ row: rows[i]!, index: i }); i += 1; continue; }
      let j = i;
      while (j < rows.length && rows[j]!.status === 'same') j += 1;
      const runLength = j - i;
      const keepStart = i === 0 ? 0 : 1;
      const keepEnd = j === rows.length ? 0 : 1;
      if (runLength <= keepStart + keepEnd + 1) {
        for (let k = i; k < j; k += 1) display.push({ row: rows[k]!, index: k });
      } else {
        for (let k = i; k < i + keepStart; k += 1) display.push({ row: rows[k]!, index: k });
        display.push({ collapsed: runLength - keepStart - keepEnd, index: i + keepStart });
        for (let k = j - keepEnd; k < j; k += 1) display.push({ row: rows[k]!, index: k });
      }
      i = j;
    }
  }

  const label = (v: Version) => `${v.version}`;
  const select = (value: string, set: (v: string) => void, id: string, text: string) => (
    <div className="flex-1 min-w-[8rem]">
      <label className="oc-label" htmlFor={id}>{text}</label>
      <select id={id} className="oc-input" value={value} onChange={(e) => set(e.target.value)}>
        {versions.map((v) => <option key={v.id} value={v.id}>{label(v)}</option>)}
      </select>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {select(fromId, setFromId, 'cmp-from', tp('policies.compareFrom'))}
        <Button onClick={() => { setFromId(toId); setToId(fromId); }}>{tp('policies.compareSwap')}</Button>
        {select(toId, setToId, 'cmp-to', tp('policies.compareTo'))}
      </div>

      {!from || !to || from.id === to.id ? (
        <p className="text-sm text-ink-secondary">{tp('policies.compareSame')}</p>
      ) : counts.changed + counts.added + counts.removed === 0 ? (
        <p className="rounded-xl bg-surface-sunken px-3 py-3 text-sm text-ink-secondary">{tp('policies.compareIdentical')}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-ink" aria-live="polite">{tp('policies.compareSummary', counts)}</p>
            <label className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer min-h-[2.75rem]">
              <input type="checkbox" className="w-4 h-4 accent-[rgb(var(--brand))]"
                checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              {tp('policies.compareShowAll')}
            </label>
          </div>
          <p className="text-xs text-ink-muted">{tp('policies.compareLegend')} {tp('policies.compareNote')}</p>

          {/* Column headings only from md up: below that each block carries its own version label. */}
          <div className="hidden md:grid grid-cols-2 gap-3 text-xs font-semibold text-ink-secondary px-1">
            <span>{from.version}</span><span>{to.version}</span>
          </div>

          <ol className="space-y-1.5">
            {display.map((entry) => {
              if ('collapsed' in entry) {
                return (
                  <li key={`c${entry.index}`} className="text-center text-xs text-ink-muted py-1 border-y border-dashed border-line">
                    ⋯ {tp('policies.compareUnchangedRun', { count: entry.collapsed })}
                  </li>
                );
              }
              const { row } = entry;
              const statusText =
                row.status === 'removed' ? tp('policies.compareRemoved')
                : row.status === 'added' ? tp('policies.compareAdded')
                : row.status === 'changed' ? tp('policies.compareChanged') : '';
              return (
                <li
                  key={entry.index}
                  className={cn(
                    'grid grid-cols-1 md:grid-cols-2 gap-1.5 md:gap-3 rounded-lg p-2 text-sm leading-relaxed',
                    row.status === 'same' && 'text-ink-secondary',
                    row.status !== 'same' && 'bg-surface-sunken border border-line',
                  )}
                >
                  {/* Status is text, not just tint: readable without colour. */}
                  {statusText && <span className="sr-only">{statusText}</span>}
                  <div className={cn(!row.left && 'hidden md:block')}>
                    {row.left && row.status !== 'same' && (
                      <span className="md:hidden text-[10px] font-semibold uppercase tracking-wide text-critical">
                        − {from.version}
                      </span>
                    )}
                    {row.left && <BlockView block={row.left} tokens={row.leftTokens}
                      side="left" />}
                  </div>
                  {/* An unchanged block is shown once on a phone: twice is only noise. */}
                  <div className={cn(!row.right && 'hidden md:block', row.status === 'same' && 'hidden md:block')}>
                    {row.right && row.status !== 'same' && (
                      <span className="md:hidden text-[10px] font-semibold uppercase tracking-wide text-good">
                        + {to.version}
                      </span>
                    )}
                    {row.right && <BlockView block={row.right} tokens={row.rightTokens} side="right" />}
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Side-by-side translation review
//
//  THE KEY FEATURE. Two language versions of the same logical policy shown at
//  once, in two columns (stacked on a phone), so a reviewer reads both and
//  checks they match. Each pane has its own language picker and loads the latest
//  version of that language; it is labelled with the language and its approval
//  state. This compares LANGUAGES, which is a different question from the
//  version Compare tab (which compares two versions of ONE language).
// ---------------------------------------------------------------------------

function ReviewPane({ pane, translations, value, onChange, otherValue, tp, formatDate }: {
  pane: 'left' | 'right';
  translations: TranslationSummary[];
  value: string;
  onChange: (id: string) => void;
  otherValue: string;
  tp: (key: PKey, options?: Record<string, unknown>) => string;
  formatDate: (iso: string) => string;
}) {
  const languageName = useLanguageName();
  const { t } = useTranslation();
  const selected = translations.find((tr) => tr.id === value);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['policy', value],
    queryFn: () => api.get<PolicyDetail>(`/policies/${value}`),
    enabled: !!value,
  });
  const latest = data?.versions?.[0];

  const labelId = `review-${pane}`;
  return (
    <section className="min-w-0 flex flex-col rounded-xl border border-line bg-surface-raised" aria-labelledby={labelId}>
      <div className="border-b border-line p-3 space-y-2">
        <label className="oc-label" htmlFor={labelId}>
          {tp(pane === 'left' ? 'policies.reviewLeft' : 'policies.reviewRight')}
        </label>
        <select
          id={labelId} className="oc-input" value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {translations.map((tr) => (
            <option key={tr.id} value={tr.id} disabled={tr.id === otherValue}>
              {languageName(tr.language)} ({tr.language.toUpperCase()})
            </option>
          ))}
        </select>
        <div className="flex flex-wrap items-center gap-2">
          {selected && <LanguageTag code={selected.language} />}
          {selected?.version && <span className="text-xs text-ink-muted">{tp('policies.viewingVersion')} {selected.version}</span>}
          {selected?.approvalStatus && selected.approvalStatus !== 'NOT_REQUIRED' && (
            <Pill tone={TONE_FOR_STATUS[selected.approvalStatus] ?? 'neutral'}>{t(`status.${selected.approvalStatus}`)}</Pill>
          )}
        </div>
      </div>
      <div className="p-3 sm:max-h-[70dvh] sm:overflow-y-auto">
        {isLoading ? (
          <Skeleton className="h-64" />
        ) : isError || !latest ? (
          <ErrorNotice message={tp('policies.reviewPaneFailed')} />
        ) : (
          <>
            {latest.createdAt && (
              <p className="text-xs text-ink-muted mb-2">
                {tp('policies.versionMeta', { date: formatDate(latest.createdAt), name: latest.createdByName ?? tp('policies.unknownAuthor') })}
              </p>
            )}
            <PolicyBody html={latest.contentHtml} />
          </>
        )}
      </div>
    </section>
  );
}

function TranslationReview({ translations, currentId, tp, formatDate }: {
  translations: TranslationSummary[];
  currentId: string;
  tp: (key: PKey, options?: Record<string, unknown>) => string;
  formatDate: (iso: string) => string;
}) {
  // Default: the current language on the left, the first other language on the right.
  const current = translations.find((tr) => tr.id === currentId) ?? translations[0]!;
  const other = translations.find((tr) => tr.id !== current.id) ?? translations[0]!;
  const [leftId, setLeftId] = useState(current.id);
  const [rightId, setRightId] = useState(other.id);

  if (translations.length < 2) {
    return <p className="text-sm text-ink-secondary">{tp('policies.reviewNeedTwo')}</p>;
  }

  // Keep the two panes on different languages when one changes to match the other.
  const changeLeft = (id: string) => { setLeftId(id); if (id === rightId) setRightId(translations.find((tr) => tr.id !== id)!.id); };
  const changeRight = (id: string) => { setRightId(id); if (id === leftId) setLeftId(translations.find((tr) => tr.id !== id)!.id); };

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-muted">{tp('policies.reviewIntro')}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
        <ReviewPane pane="left" translations={translations} value={leftId} onChange={changeLeft} otherValue={rightId} tp={tp} formatDate={formatDate} />
        <ReviewPane pane="right" translations={translations} value={rightId} onChange={changeRight} otherValue={leftId} tp={tp} formatDate={formatDate} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  The page
// ---------------------------------------------------------------------------

type Tab = 'view' | 'edit' | 'compare' | 'review';

/** "1.0" -> "1.1"; steps past any version that already exists. */
function suggestNextVersion(existing: string[], latest: string): string {
  const match = /^(.*?)(\d+)$/.exec(latest);
  let candidate = match ? `${match[1]}${Number(match[2]) + 1}` : `${latest}.1`;
  for (let i = 0; i < 100 && existing.includes(candidate); i += 1) {
    const m = /^(.*?)(\d+)$/.exec(candidate);
    candidate = m ? `${m[1]}${Number(m[2]) + 1}` : `${candidate}.1`;
  }
  return candidate;
}

export function PolicyEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { allows, session } = useAuth();
  const queryClient = useQueryClient();
  const describeApiError = useApiError();

  const tp = useCallback(
    (key: PKey, options?: Record<string, unknown>) =>
      t(key, { defaultValue: POLICY_EDITOR_STRINGS[key], ...options }),
    [t],
  );

  const errorText = useCallback((err: unknown): string => {
    if (err instanceof ApiError) {
      const key = `policies.error.${err.code}` as PKey;
      if (key in POLICY_EDITOR_STRINGS) return tp(key);
    }
    return describeApiError(err);
  }, [tp, describeApiError]);

  const navigate = useNavigate();
  const languageName = useLanguageName();
  const canEdit = allows('policy.update');
  const canCreate = allows('policy.create');
  const me = session?.user.id ?? null;

  const [tab, setTab] = useState<Tab>('view');
  const [viewId, setViewId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addingLanguage, setAddingLanguage] = useState(false);
  const [newLanguage, setNewLanguage] = useState('');

  const { data: policy, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['policy', id],
    queryFn: () => api.get<PolicyDetail>(`/policies/${id}`),
    enabled: !!id,
  });

  // People who can be named approver: active members whose ROLE carries
  // policy.approve. Listing members needs user.read; without it the picker is
  // simply unavailable and the version saves without an approver.
  const { data: members, isError: membersUnavailable } = useQuery({
    queryKey: ['users', 'approver-candidates'],
    queryFn: () => api.get<{ items: Member[] }>('/users?pageSize=100'),
    enabled: canEdit && allows('user.read'),
    retry: false,
  });
  const approvers = useMemo(() => {
    if (!members || !session) return [];
    return members.items.filter((m) => m.isActive && can({
      userId: m.id,
      organizationId: session.organization.id,
      role: m.role as never,
      tier: session.organization.tier,
    }, 'policy.approve'));
  }, [members, session]);
  const canPickApprover = canEdit && allows('user.read') && !membersUnavailable;

  const versions = policy?.versions ?? [];
  const latest = versions[0];
  const viewed = versions.find((v) => v.id === viewId) ?? latest;

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));

  // --- Editor state ----------------------------------------------------------

  const editorRef = useRef<HTMLDivElement | null>(null);
  const [baseId, setBaseId] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [status, setStatus] = useState<EditorStatus>({ changed: false, empty: true, length: 0 });
  const [versionLabel, setVersionLabel] = useState('');
  const [versionTouched, setVersionTouched] = useState(false);
  const [approverId, setApproverId] = useState('');
  const [pendingBase, setPendingBase] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const base = versions.find((v) => v.id === baseId) ?? latest;

  // Suggested number follows the latest version until the user types their own.
  useEffect(() => {
    if (!latest || versionTouched) return;
    setVersionLabel(suggestNextVersion(versions.map((v) => v.version), latest.version));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest?.id, versionTouched]);

  // Warn before a reload/close throws away unsaved edits. (In-app navigation
  // cannot be intercepted with this router setup; the banner says so instead.)
  useEffect(() => {
    if (!status.changed) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [status.changed]);

  const loadBase = (versionId: string) => {
    setBaseId(versionId);
    setEditorKey((k) => k + 1); // remount the editor with the other version's text
    setStatus({ changed: false, empty: false, length: 0 });
    setPendingBase(null);
  };
  const requestBase = (versionId: string) => {
    if (status.changed) setPendingBase(versionId);
    else loadBase(versionId);
  };

  const save = useMutation({
    mutationFn: (body: { version: string; contentHtml: string; approverId: string | null }) =>
      api.post<Version & { attributionRestored?: boolean }>(`/policies/${id}/versions`, body),
    onSuccess: async (created) => {
      // Wait for the refreshed list before pointing the editor at the new row.
      await queryClient.invalidateQueries({ queryKey: ['policy', id] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      // ShareAlike guard: the server put the source/licence notice back because the
      // edit had removed it. Say so, so the reappearing block is not a surprise.
      const saved = tp('policies.saved', { version: created.version });
      setNotice(
        created.attributionRestored
          ? `${saved} ${t('policies.license.restored', {
              defaultValue: LICENSE_ATTRIBUTION_STRINGS['policies.license.restored'],
              license: policy?.template?.license === 'CC-BY-SA-4.0' ? 'CC BY-SA 4.0' : (policy?.template?.license ?? 'its source licence'),
            })}`
          : saved,
      );
      setError(null);
      setFormError(null);
      setVersionTouched(false);
      setApproverId('');
      setViewId(created.id);
      loadBase(created.id);
      setTab('view');
    },
    onError: (err) => setFormError(errorText(err)),
  });

  const submitVersion = () => {
    setFormError(null);
    setNotice(null);
    const el = editorRef.current;
    if (!el || !latest) return;

    const label = versionLabel.trim();
    if (label.length < 3 || label.length > 20) { setFormError(tp('policies.versionLength')); return; }
    if (versions.some((v) => v.version === label)) { setFormError(tp('policies.versionTaken', { version: label })); return; }

    // Rewrite the editor to exactly what will be stored, and say if that
    // removed anything, so a silent server-side strip never looks like loss.
    const { root, removed } = sanitizeToElement(el.innerHTML);
    const html = root.innerHTML;
    if (isEffectivelyEmpty(html)) { setFormError(tp('policies.emptyContent')); return; }
    if (html.length > MAX_CONTENT_CHARS) {
      setFormError(tp('policies.tooLong', { count: html.length, max: MAX_CONTENT_CHARS }));
      return;
    }
    if (removed > 0) {
      el.replaceChildren(...Array.from(root.childNodes).map((n) => document.importNode(n, true)));
      setNotice(tp('policies.removedNotice', { count: removed }));
    }
    save.mutate({ version: label, contentHtml: html, approverId: approverId || null });
  };

  // --- Approval --------------------------------------------------------------

  const [decision, setDecision] = useState<{ version: Version; approve: boolean } | null>(null);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: ({ versionId, approve, text }: { versionId: string; approve: boolean; text: string }) =>
      api.post(`/policies/versions/${versionId}/approval`, {
        approve,
        ...(text.trim() ? { comment: text.trim() } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['policy', id] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      setDecision(null);
      setComment('');
      setNotice(tp('policies.decisionRecorded'));
    },
    onError: (err) => {
      setDecision(null);
      setError(errorText(err));
    },
  });

  const openDecision = (version: Version, approve: boolean) => {
    setComment('');
    setCommentError(null);
    setDecision({ version, approve });
  };
  const confirmDecision = () => {
    if (!decision) return;
    if (!decision.approve && !comment.trim()) { setCommentError(tp('policies.commentRequired')); return; }
    decide.mutate({ versionId: decision.version.id, approve: decision.approve, text: comment });
  };

  // --- Linked translations ---------------------------------------------------

  const addTranslation = useMutation({
    mutationFn: (language: string) =>
      api.post<PolicyDetail & { groupId: string }>(`/policies/${id}/translations`, { language }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      void queryClient.invalidateQueries({ queryKey: ['policy', id] });
      setAddingLanguage(false);
      setNewLanguage('');
      setError(null);
      setTab('view');
      setViewId(null);
      // Open the brand-new draft. Same route, new param: the queries refetch.
      navigate(`/policies/${created.id}`);
      setNotice(tp('policies.addedNotice', { name: languageName(created.language) }));
    },
    onError: (err) => setError(errorText(err)),
  });

  // --- Render ----------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="oc-page space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-80" />
      </div>
    );
  }

  if (loadError || !policy || !latest || !viewed) {
    const notFound = loadError instanceof ApiError && loadError.status === 404;
    return (
      <div className="oc-page space-y-4">
        <Link to="/policies" className="text-xs text-ink-muted hover:text-ink-secondary">← {tp('policies.backToList')}</Link>
        <ErrorNotice
          message={notFound ? tp('policies.notFound') : tp('policies.loadFailed')}
          onRetry={notFound ? undefined : () => void refetch()}
          retryLabel={t('common.retry')}
        />
      </div>
    );
  }

  // The group's language versions. A standalone policy comes back with an empty
  // list; treat it as a group of one (this policy) so the UI is uniform.
  const translations: TranslationSummary[] = policy.translations.length
    ? policy.translations
    : [{ id: policy.id, language: policy.language, name: policy.name, version: latest.version, approvalStatus: latest.approval?.status ?? null, isActive: policy.isActive, isCurrent: true }];
  const presentLanguages = new Set(translations.map((tr) => tr.language));
  const missingLanguages = LANGUAGES.filter((code) => !presentLanguages.has(code));
  const hasMultipleLanguages = translations.length >= 2;

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'view', label: tp('policies.tabView') },
    ...(canEdit ? [{ key: 'edit' as const, label: tp('policies.tabEdit') }] : []),
    { key: 'compare', label: tp('policies.tabCompare') },
    ...(hasMultipleLanguages ? [{ key: 'review' as const, label: tp('policies.tabReview') }] : []),
  ];

  const approval = viewed.approval;
  const approverName = approval?.approver ? `${approval.approver.firstName} ${approval.approver.lastName}` : null;
  const isAssignedApprover =
    !!approval && approval.status === 'PENDING' && approval.approverId === me && allows('policy.approve');

  return (
    <div className="oc-page space-y-4 animate-fade-in pb-6">
      <header>
        <Link to="/policies" className="text-xs text-ink-muted hover:text-ink-secondary">← {tp('policies.backToList')}</Link>
        <div className="flex flex-wrap items-start justify-between gap-2 mt-1">
          <div className="min-w-0">
            <h1 className="oc-h1 break-words">
              {policy.name}
              {/* Inline with the title, so the language is never off-screen. */}
              <LanguageTag code={policy.language} className="ml-2 align-middle" />
            </h1>
            <p className="text-xs text-ink-muted mt-1">
              {policy.documentCode && <span className="font-mono mr-2">{policy.documentCode}</span>}
              {tp('policies.currentVersion', { version: latest.version })}
            </p>
          </div>
          {latest.approval && (
            <Pill tone={TONE_FOR_STATUS[latest.approval.status] ?? 'neutral'}>
              {t(`status.${latest.approval.status}`)}
            </Pill>
          )}
        </div>
      </header>

      {/* ----------------------------------------------------------------
          Linked-language switcher. The group's languages as pills; the one
          being viewed is highlighted, the others open that language's policy.
          "Add a language" creates a linked draft in a language not yet present.
          ---------------------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{tp('policies.languages')}</span>
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={tp('policies.languages')}>
          {translations.map((tr) =>
            tr.isCurrent ? (
              <span
                key={tr.id}
                title={tp('policies.thisLanguage', { name: languageName(tr.language) })}
                className="inline-flex items-center gap-1 rounded-lg border border-brand bg-brand/10 px-2 py-1 text-xs font-semibold text-brand"
              >
                <LanguageTag code={tr.language} />
              </span>
            ) : (
              <Link
                key={tr.id}
                to={`/policies/${tr.id}`}
                title={tp('policies.switchLanguage', { name: languageName(tr.language) })}
                aria-label={tp('policies.switchLanguage', { name: languageName(tr.language) })}
                className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-xs text-ink-secondary hover:border-brand/40 hover:text-ink"
              >
                <LanguageTag code={tr.language} />
                {tr.approvalStatus && tr.approvalStatus !== 'NOT_REQUIRED' && (
                  <Pill tone={TONE_FOR_STATUS[tr.approvalStatus] ?? 'neutral'}>{t(`status.${tr.approvalStatus}`)}</Pill>
                )}
              </Link>
            ),
          )}
          {canCreate && (
            <Button size="sm" onClick={() => { setAddingLanguage(true); setNewLanguage(missingLanguages[0] ?? ''); setError(null); }}>
              + {tp('policies.addLanguage')}
            </Button>
          )}
        </div>
      </div>

      {notice && (
        <div role="status" className="rounded-xl border border-good/30 bg-good/[0.07] px-4 py-3 text-sm text-ink flex items-start justify-between gap-3">
          <span>{notice}</span>
          <button className="text-xs font-medium text-brand hover:underline shrink-0" onClick={() => setNotice(null)}>
            {t('common.close')}
          </button>
        </div>
      )}
      {error && <ErrorNotice message={error} />}

      {/* Tabs. A segmented control that scrolls if a language makes labels long. */}
      <div role="tablist" aria-label={policy.name} className="flex gap-1 overflow-x-auto border-b border-line">
        {tabs.map((entry) => (
          <button
            key={entry.key}
            role="tab"
            id={`pe-tab-${entry.key}`}
            aria-selected={tab === entry.key}
            aria-controls={`pe-panel-${entry.key}`}
            onClick={() => setTab(entry.key)}
            className={cn(
              'px-4 min-h-[2.75rem] text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors',
              tab === entry.key ? 'border-brand text-brand' : 'border-transparent text-ink-secondary hover:text-ink',
            )}
          >
            {entry.label}
            {entry.key === 'edit' && status.changed && (
              <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-warning align-middle" title={tp('policies.unsavedChanges')} />
            )}
          </button>
        ))}
      </div>

      {/* ---------------- READ ---------------- */}
      <div role="tabpanel" id="pe-panel-view" aria-labelledby="pe-tab-view" hidden={tab !== 'view'} className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
            <div>
              <label className="oc-label" htmlFor="pe-view-version">{tp('policies.viewingVersion')}</label>
              <select
                id="pe-view-version" className="oc-input sm:!w-auto" value={viewed.id}
                onChange={(e) => setViewId(e.target.value)}
              >
                {versions.map((v) => <option key={v.id} value={v.id}>{v.version}</option>)}
              </select>
            </div>
            <p className="text-xs text-ink-muted">
              {tp('policies.versionMeta', {
                date: formatDate(viewed.createdAt),
                name: viewed.createdByName ?? tp('policies.unknownAuthor'),
              })}
            </p>
          </div>
          <PolicyBody html={viewed.contentHtml} />
        </Card>

        {/* Structured source/licence of the template this policy came from. Renders
            nothing for our own templates. The same notice is inside the body above. */}
        {policy.template && (
          <LicenseAttribution
            variant="panel"
            license={policy.template.license}
            sourceUrl={policy.template.sourceUrl}
            sourceAttribution={policy.template.sourceAttribution}
            modifiedFromSource={policy.template.modifiedFromSource}
          />
        )}

        <Card>
          <CardHeader title={tp('policies.approval')} subtitle={`${tp('policies.viewingVersion')} ${viewed.version}`} />
          {!approval || approval.status === 'NOT_REQUIRED' ? (
            <p className="text-sm text-ink-secondary">{tp('policies.approvalNotRequired')}</p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone={TONE_FOR_STATUS[approval.status] ?? 'neutral'}>{t(`status.${approval.status}`)}</Pill>
                <span className="text-sm text-ink-secondary">
                  {approval.status === 'PENDING'
                    ? tp('policies.approvalPending', { name: approverName ?? tp('policies.noApprover') })
                    : approval.decidedAt
                      ? tp('policies.approvalDecidedBy', {
                          status: t(`status.${approval.status}`),
                          name: approverName ?? tp('policies.unknownAuthor'),
                          date: formatDate(approval.decidedAt),
                        })
                      : null}
                </span>
              </div>
              {approval.comment && (
                <blockquote className="border-l-2 border-line pl-3 text-sm text-ink-secondary">
                  <span className="block text-xs text-ink-muted">{tp('policies.approvalComment')}</span>
                  {/* Plain text child: React escapes it. Comments are user input. */}
                  <span className="whitespace-pre-wrap break-words">{approval.comment}</span>
                </blockquote>
              )}
              {isAssignedApprover && (
                <div className="rounded-xl bg-surface-sunken p-3 space-y-3">
                  <p className="text-sm text-ink">{tp('policies.approvalYourTurn')}</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button variant="primary" onClick={() => openDecision(viewed, true)}>{t('policies.approve', { defaultValue: 'Approve' })}</Button>
                    <Button variant="danger" onClick={() => openDecision(viewed, false)}>{t('policies.reject', { defaultValue: 'Reject' })}</Button>
                  </div>
                  <p className="text-xs text-ink-muted">{tp('policies.decisionFinal')}</p>
                </div>
              )}
            </div>
          )}
        </Card>

        <Card>
          <CardHeader title={t('policies.versions', { defaultValue: 'Version history' })} subtitle={tp('policies.historyHint')} />
          <ul className="divide-y divide-line -mx-1">
            {versions.map((v, index) => (
              <li key={v.id} className="py-3 px-1 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">
                      {v.version}
                      {index === 0 && <span className="ml-2 oc-pill-neutral">{tp('policies.latestBadge')}</span>}
                    </p>
                    <p className="text-xs text-ink-muted mt-0.5">
                      {formatDate(v.createdAt)} · {t('policies.createdBy', { name: v.createdByName ?? tp('policies.unknownAuthor') })}
                    </p>
                  </div>
                  {v.approval && (
                    <Pill tone={TONE_FOR_STATUS[v.approval.status] ?? 'neutral'}>{t(`status.${v.approval.status}`)}</Pill>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Button size="sm" onClick={() => { setViewId(v.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                    {tp('policies.viewThis')}
                  </Button>
                  {canEdit && (
                    <Button size="sm" onClick={() => { requestBase(v.id); setTab('edit'); }}>
                      {tp('policies.startFromThis')}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* ---------------- EDIT ----------------
          Kept mounted (just hidden) when another tab is open, so switching tabs
          to check the history never throws away what has been typed. */}
      {canEdit && (
        <div role="tabpanel" id="pe-panel-edit" aria-labelledby="pe-tab-edit" hidden={tab !== 'edit'} className="space-y-4">
          <div className="rounded-xl border border-brand/30 bg-brand/[0.06] px-4 py-3">
            <p className="text-sm font-medium text-ink">{tp('policies.immutableTitle')}</p>
            <p className="text-sm text-ink-secondary mt-0.5 leading-relaxed">{tp('policies.immutableBody')}</p>
          </div>

          <Card className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <label className="oc-label" htmlFor="pe-base">{tp('policies.baseVersion')}</label>
                <select
                  id="pe-base" className="oc-input sm:!w-auto" value={base?.id ?? ''}
                  onChange={(e) => requestBase(e.target.value)}
                >
                  {versions.map((v) => <option key={v.id} value={v.id}>{v.version}</option>)}
                </select>
              </div>
              {status.changed && <span className="oc-pill-warning">{tp('policies.unsavedChanges')}</span>}
            </div>

            {base && (
              <RichTextEditor
                key={editorKey}
                initialHtml={base.contentHtml}
                editorRef={editorRef}
                onStatus={setStatus}
                tp={tp}
                disabled={save.isPending}
              />
            )}
            <p className="text-xs text-ink-muted leading-relaxed">{tp('policies.supportedNote')}</p>
          </Card>

          <Card>
            <CardHeader title={tp('policies.saveTitle')} />
            <form
              onSubmit={(e) => { e.preventDefault(); submitVersion(); }}
              className="max-w-md"
              noValidate
            >
              <Field label={tp('policies.versionLabel')} hint={tp('policies.versionHint')} htmlFor="pe-version" required>
                <input
                  id="pe-version" className="oc-input" value={versionLabel} maxLength={20}
                  onChange={(e) => { setVersionLabel(e.target.value); setVersionTouched(true); }}
                />
              </Field>

              {canPickApprover ? (
                <Field label={tp('policies.approverLabel')} hint={tp('policies.approverHint')} htmlFor="pe-approver">
                  <select id="pe-approver" className="oc-input" value={approverId} onChange={(e) => setApproverId(e.target.value)}>
                    <option value="">{tp('policies.approverNone')}</option>
                    {approvers.map((m) => (
                      <option key={m.id} value={m.id}>{m.firstName} {m.lastName} ({m.email})</option>
                    ))}
                  </select>
                </Field>
              ) : (
                <p className="mb-4 text-xs text-ink-muted">{tp('policies.approverUnavailable')}</p>
              )}

              {formError && <div className="mb-3"><ErrorNotice message={formError} /></div>}
              {!status.changed && !formError && (
                <p className="mb-3 text-xs text-ink-muted">{tp('policies.nothingChanged')}</p>
              )}

              <Button
                type="submit" variant="primary" className="w-full sm:w-auto"
                disabled={save.isPending || !status.changed}
              >
                {save.isPending ? t('common.saving') : tp('policies.saveVersion')}
              </Button>
            </form>
          </Card>
        </div>
      )}

      {/* ---------------- COMPARE ---------------- */}
      <div role="tabpanel" id="pe-panel-compare" aria-labelledby="pe-tab-compare" hidden={tab !== 'compare'}>
        <Card>
          {tab === 'compare' && <CompareView versions={versions} tp={tp} />}
        </Card>
      </div>

      {/* ---------------- REVIEW TRANSLATIONS (side by side) ---------------- */}
      {hasMultipleLanguages && (
        <div role="tabpanel" id="pe-panel-review" aria-labelledby="pe-tab-review" hidden={tab !== 'review'}>
          <Card>
            {tab === 'review' && (
              <TranslationReview translations={translations} currentId={policy.id} tp={tp} formatDate={formatDate} />
            )}
          </Card>
        </div>
      )}

      {/* Add a language */}
      <Modal
        open={addingLanguage}
        onClose={() => setAddingLanguage(false)}
        title={tp('policies.addLanguageTitle')}
        footer={
          <>
            <Button onClick={() => setAddingLanguage(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={addTranslation.isPending || !newLanguage || missingLanguages.length === 0}
              onClick={() => newLanguage && addTranslation.mutate(newLanguage)}
            >
              {addTranslation.isPending ? tp('policies.addLanguageCreating') : tp('policies.addLanguageSubmit')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-secondary">{tp('policies.addLanguageBody')}</p>
          {missingLanguages.length === 0 ? (
            <p className="text-sm text-ink">{tp('policies.addLanguageNoneLeft')}</p>
          ) : (
            <Field label={tp('policies.addLanguageSelect')} htmlFor="pe-add-language">
              <select
                id="pe-add-language" className="oc-input" value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
              >
                {missingLanguages.map((code) => (
                  <option key={code} value={code}>{languageName(code)} ({code.toUpperCase()})</option>
                ))}
              </select>
            </Field>
          )}
        </div>
      </Modal>

      {/* Discard confirmation */}
      <Modal
        open={pendingBase !== null}
        onClose={() => setPendingBase(null)}
        title={tp('policies.discardTitle')}
        footer={
          <>
            <Button onClick={() => setPendingBase(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" onClick={() => pendingBase && loadBase(pendingBase)}>
              {tp('policies.discardConfirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-secondary">{tp('policies.discardBody')}</p>
      </Modal>

      {/* Approval decision */}
      <Modal
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={decision
          ? tp(decision.approve ? 'policies.approveTitle' : 'policies.rejectTitle', { version: decision.version.version })
          : ''}
        footer={
          <>
            <Button onClick={() => setDecision(null)}>{t('common.cancel')}</Button>
            <Button
              variant={decision?.approve ? 'primary' : 'danger'}
              disabled={decide.isPending}
              onClick={confirmDecision}
            >
              {decide.isPending
                ? t('common.saving')
                : decision?.approve ? t('policies.approve', { defaultValue: 'Approve' }) : t('policies.reject', { defaultValue: 'Reject' })}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-secondary">{tp('policies.decisionFinal')}</p>
          <Field
            label={tp('policies.commentLabel')}
            hint={decision?.approve ? tp('policies.commentApproveHint') : tp('policies.commentRejectHint')}
            error={commentError ?? undefined}
            htmlFor="pe-comment"
          >
            <textarea
              id="pe-comment" className="oc-input min-h-[6rem] py-2 resize-y" maxLength={2000}
              value={comment} onChange={(e) => { setComment(e.target.value); setCommentError(null); }}
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}
